import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { secretSantaApi } from '@/shared/api/secretSanta'
import { useUserStore } from '@/entities/user/model/userStore'
import type { SantaCard, SecretSanta } from './types'

const POLL_INTERVAL = 30_000

export const useSecretSantaStore = defineStore('secretSanta', () => {
  const data = ref<SecretSanta | null>(null)
  const status = ref<'unknown' | 'loading' | 'available' | 'unavailable' | 'error'>('unknown')
  const errorMessage = ref('')
  const revealedLocally = ref<number[]>([])
  let pollTimer: ReturnType<typeof setInterval> | null = null

  const isAvailable = computed(() => status.value === 'available' && data.value !== null)
  const isFilled = computed(() => isAvailable.value && !!data.value?.card.isFilled)
  const isDistributed = computed(() => !!data.value?.isDistributed)
  const isParticipant = computed(() => isDistributed.value && data.value?.recipient !== null)
  const unreadFromSanta = computed(() => data.value?.card.unreadMessages ?? 0)
  const unreadFromRecipient = computed(() => data.value?.recipient?.card.unreadMessages ?? 0)
  const unreadTotal = computed(() => unreadFromSanta.value + unreadFromRecipient.value)
  const notificationsEnabled = computed(() => !!data.value?.notificationsEnabled)

  const isRevealed = computed(() => {
    const recipient = data.value?.recipient
    if (!recipient) return false
    return !!recipient.revealedAt || revealedLocally.value.includes(recipient.card.id)
  })

  async function load(silent = false) {
    const userStore = useUserStore()
    if (!userStore.isAuth) {
      reset()
      return
    }
    if (!silent) status.value = status.value === 'available' ? 'available' : 'loading'
    try {
      const { santa } = await secretSantaApi.get()
      data.value = santa ?? null
      status.value = santa ? 'available' : 'unavailable'
      errorMessage.value = ''
    } catch (e) {
      if (status.value !== 'available') {
        status.value = 'error'
        errorMessage.value = e.message ?? 'Не удалось загрузить Тайного Санту'
      }
    }
  }

  function setCard(card: SantaCard) {
    if (!data.value) return
    data.value.card = { ...card, unreadMessages: data.value.card.unreadMessages ?? 0 }
  }

  function markChatRead(chatId: number) {
    if (!data.value) return
    if (data.value.card.id === chatId) data.value.card.unreadMessages = 0
    if (data.value.recipient?.card.id === chatId) data.value.recipient.card.unreadMessages = 0
  }

  async function setNotifications(enabled: boolean) {
    if (!data.value) return
    const prev = data.value.notificationsEnabled
    data.value.notificationsEnabled = enabled
    try {
      const result = await secretSantaApi.setNotifications(enabled)
      if (data.value) data.value.notificationsEnabled = result.notificationsEnabled
    } catch (e) {
      if (data.value) data.value.notificationsEnabled = prev
      throw e
    }
  }

  async function markRevealed() {
    const recipient = data.value?.recipient
    if (!recipient || isRevealed.value) return
    revealedLocally.value = [...revealedLocally.value, recipient.card.id]
    try {
      await secretSantaApi.reveal()
      if (data.value?.recipient && !data.value.recipient.revealedAt) {
        data.value.recipient.revealedAt = new Date().toISOString()
      }
    } catch (e) {
      console.error('Не удалось сохранить раскрытие получателя', e)
    }
  }

  async function markSent() {
    if (!data.value?.recipient) return
    const result = await secretSantaApi.markSent()
    if (data.value?.recipient) data.value.recipient.card.isSent = result.isSent
  }

  function refreshUnread() {
    if (document.hidden || !isDistributed.value) return
    load(true)
  }

  function onVisibilityChange() {
    if (!document.hidden) refreshUnread()
  }

  function startPolling() {
    stopPolling()
    pollTimer = setInterval(refreshUnread, POLL_INTERVAL)
    document.addEventListener('visibilitychange', onVisibilityChange)
  }

  function stopPolling() {
    if (pollTimer) clearInterval(pollTimer)
    pollTimer = null
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }

  function reset() {
    stopPolling()
    data.value = null
    status.value = 'unknown'
    errorMessage.value = ''
  }

  return {
    data,
    status,
    errorMessage,
    isAvailable,
    isFilled,
    isDistributed,
    isParticipant,
    isRevealed,
    unreadFromSanta,
    unreadFromRecipient,
    unreadTotal,
    notificationsEnabled,
    load,
    refreshUnread,
    setCard,
    markChatRead,
    setNotifications,
    markRevealed,
    markSent,
    startPolling,
    stopPolling,
    reset,
  }
})
